import {
  arbeitsRang,
  dauerMinutenVon,
  dauerstufeVon,
  istWerktag,
  type Groessendauern,
  type Tagesfenster,
  type Termin,
} from '@netcon-work/shared'

/**
 * **Der Tagesplan – freie Lücken finden und Aufgaben hineinlegen**
 * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.6).
 *
 * Nils, 30.09.2026: *„schau dir mal usemotion an … wenn eine aufgabe unerledigt ist, verschieb
 * das in die passenden slots im kalender."*
 *
 * ## Warum das hier gerechnet wird und nicht auf dem Server
 *
 * Wie beim Daily ([`daily-runde.ts`](./daily-runde.ts)): Der Aufgabenbestand liegt vollständig
 * im Client ([Leitplanke 2](../../../../../CLAUDE.md)), die Termine des Tages kommen über eine
 * kleine Abfrage. Neu zu planen ist damit ein Neuzeichnen – kein Ladezustand, kein Spinner.
 *
 * ## Warum reine Funktionen
 *
 * `jetzt` und die Termine werden hereingegeben, nicht hier geholt. Eine Funktion, die die Uhr
 * selbst liest, lässt sich nicht prüfen – und ein Plan, der im Test von der Tageszeit abhängt,
 * ist genau die Sorte Test, die irgendwann nachts rot wird.
 *
 * ## Was hier **nicht** passiert
 *
 * **Nichts wird in den Kalender zurückgeschrieben** (Nils: *„dafür ändert sich ja zu dynamisch
 * und zu viel von den aufgaben her"*). Der Plan lebt in Work; der Kalender wird nur gelesen.
 */

/**
 * **Was eine Größe je Arbeitswelt dauert** – Kennung der Welt auf ihre Überschreibungen.
 *
 * Eine Tafel statt eines Felds an jeder Aufgabe: Die Zeilen kommen aus dem Bestand, die Dauern
 * aus der Welt-Liste, und beide werden erst im Plan zusammengeführt. Die Dauer an jede Zeile zu
 * schreiben hieße, sie bei jeder Änderung an hunderten Stellen nachzuziehen.
 */
export type Weltdauern = Record<string, Groessendauern>

/** Ein Zeitraum in Stunden seit Mitternacht – `9.5` ist halb zehn. */
export interface Spanne {
  von: number
  bis: number
}

export interface Kalendertermin {
  betreff: string
  beginn: string
  ende: string
  ganztaegig: boolean
  zeigtAls: string
}

export interface PlanAufgabe {
  id: string
  title: string
  groesse: string | null
  estimateMinutes?: number | null
  priority?: string | null
  dueAt?: string | null
  updatedAt?: string
  workspaceId?: string | null
  /**
   * **Der wirksame Termin** (Z-20, 06.10.2026) – Fälligkeit → Meilenstein → Zieltermin →
   * Zeitfenster. Fehlt er, gilt `dueAt`. Erst damit sortiert der Kalender wie der Vorlauf.
   */
  termin?: Termin
  sourceCreatedAt?: string | Date | null
  createdAt?: string | Date | null
}

/**
 * `markiert` – für diesen Tag vorgenommen (Z-20): vor den Vorschlägen, nach dem Laufenden.
 * `fest` – an eine Uhrzeit gezogen (13.10.2026): steht, wo man sie hingelegt hat.
 */
export type BlockArt = 'termin' | 'grundlast' | 'aufgabe' | 'markiert' | 'fest' | 'vorschlag'

/**
 * **Eine Aufgabe mit fester Uhrzeit** (Nils, 13.10.2026: *„in Blocker auch aufgaben legen die ich
 * mir im kalender extra dafür angelegt habe … termine im kalender wo ich parallel noch 1-2 sachen
 * mach kann, dann kann ich die dahinziehen auch wenn eigentlich kein Platz im kalender wäre"*).
 */
export interface FesteAufgabe extends PlanAufgabe {
  /** Beginn in Stunden seit Mitternacht – `9.5` ist halb zehn. */
  um: number
}

/** Eine freie Lücke, zusammen mit dem Fenster, aus dem sie stammt. */
interface FreieLuecke extends Spanne {
  weltId: string | null
}

/**
 * **Die Planung eines Tages** ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md)
 * AP 19.4, 01.10.2026).
 *
 * Ein Tag hat seit heute nicht mehr **ein** Fenster, sondern mehrere – Nils arbeitet in PRIVAT
 * samstags und werktags abends, in den übrigen Welten tagsüber. In jedes Fenster darf nur, was
 * zu seiner Welt gehört.
 */
export interface Tagesplanung {
  fenster: readonly Tagesfenster[]
  /**
   * **Ab wann überhaupt noch geplant werden darf** – Stunde als Dezimalzahl, `undefined` für
   * einen Tag, der ganz vor einem liegt (Nils, 01.10.2026: *„dann können die freien Lücken nicht
   * neu vergeben werden, wenn sie in der Vergangenheit liegen. So funktioniert Zeit nicht"*).
   *
   * Der Plan hat vormittags Freigewordenes am Nachmittag wieder angeboten – und umgekehrt eine
   * Lücke um neun Uhr noch um vier Uhr nachmittags als Vorschlag gezeichnet. Beides ist nicht
   * falsch gerechnet, sondern **nicht gemeint**: Ein Plan ist eine Aussage über das, was noch
   * kommt.
   *
   * **Termine und Grundlast bleiben trotzdem stehen**, auch wenn sie vorbei sind. Sie sind keine
   * Vorschläge, sondern das, was der Tag war.
   */
  abStunde?: number
  /** Einmal am Tag, aus dem allgemeinen Fenster – nicht je Welt. */
  grundlast: number
  /** Die Welten mit eigener Zeit; alle anderen teilen sich das allgemeine Fenster. */
  mitEigenerZeit: ReadonlySet<string>
}

/**
 * **Ein einzelnes Fenster als Planung** – der einfache Fall, und der Weg, auf dem die
 * bestehenden Aufrufe unverändert weiterlaufen.
 */
export function einFenster(zeit: Arbeitszeit): Tagesplanung {
  return {
    fenster: [{ von: zeit.von, bis: zeit.bis, weltId: null }],
    grundlast: zeit.grundlast,
    mitEigenerZeit: new Set(),
  }
}

export interface Block extends Spanne {
  art: BlockArt
  titel: string
  aufgabe?: PlanAufgabe
  /** Ein Stück einer geteilten Aufgabe: „Teil 1/2" (Z-20). Fehlt bei Ganzem. */
  teil?: { nr: number; von: number }
}

export interface Tagesplan {
  /** Wie viele Vorschläge in keine Lücke passten – die Erklärung für einen leer wirkenden Tag. */
  nichtPlatziert: number
  /** Die längste noch freie Strecke in Stunden. */
  laengsteLuecke: number
  bloecke: Block[]
  /** Was heute nicht mehr hineinpasst – mit Absicht sichtbar, statt still zu verschwinden. */
  rest: PlanAufgabe[]
  /**
   * **Für diesen Tag markiert, passt aber nicht** (Z-20). Getrennt von `rest`: das eine ist
   * angefangene Arbeit, das andere eine Absicht – beide sichtbar, keine still verschwunden.
   */
  markiertPasstNicht: PlanAufgabe[]
  /** Freie Stunden **nach** Abzug der Termine, **vor** Abzug der Grundlast. */
  freiRoh: number
  /** Was davon nach der Grundlast übrig ist – die Zahl, an der eine Entscheidung hängt. */
  freiNetto: number
}

export interface Arbeitszeit {
  von: number
  bis: number
  /** Abzug je Arbeitstag für Mail, Telefon, Unterbrechung – in Stunden. */
  grundlast: number
}

/**
 * **Mo–Fr 8–17 Uhr, anderthalb Stunden Grundlast** ([E-375](../../../../../docs/ENTSCHEIDUNGEN.md#e-375)).
 *
 * Arbeitszeiten je Person gibt es noch nicht (AP 19.4). Bis dahin ist das die Vorgabe – und sie
 * steht hier als **benannte Größe** statt als Zahl im Code, damit die spätere Einstellung nur
 * diesen einen Wert ersetzt.
 *
 * **Die Grundlast ist kein Termin und keine Aufgabe.** Mail, Telefonate und Unterbrechungen als
 * Zeilen zu führen wäre Pflege ohne Nutzen; als Abzug machen sie die Zahl „frei heute" ehrlich.
 */
export const ARBEITSZEIT_VORGABE: Arbeitszeit = { von: 8, bis: 17, grundlast: 1.5 }

/** Mindestens eine halbe Stunde – kürzere Splitter sind keine Arbeitszeit, sondern Rest. */
export const MINDESTBLOCK = 0.5

/**
 * **Eine Uhrzeit als Stunden seit Mitternacht.**
 *
 * Die Termine kommen als ISO **ohne Versatz** in der angefragten Zeitzone
 * (`2026-09-15T09:30:00.0000000`, siehe `outlook/graph.ts`). Genau deshalb wird hier auf den
 * Zeichen gearbeitet und **nicht** `new Date()` benutzt: Der Browser läse die Zeichenkette als
 * Ortszeit, und im Sommer stünde jeder Termin zwei Stunden daneben – dieselbe Fehlerklasse, die
 * in `abnahme-erinnerung.ts` schon zwei Stunden Suche gekostet hat.
 */
export function stundeAus(iso: string): number | null {
  const treffer = /T(\d{2}):(\d{2})/.exec(iso)
  if (!treffer) return null
  return Number(treffer[1]) + Number(treffer[2]) / 60
}

/**
 * **Was der Kalender blockiert.**
 *
 * `zeigtAls === 'free'` blockiert **nicht**: Wer einen Termin auf „frei" stellt, sagt damit, dass
 * er daneben arbeiten kann – meistens ein Geburtstag oder eine Erinnerung. Ganztägige Einträge
 * ebenso: Ein ganztägiges „Homeoffice" ist kein belegter Tag.
 */
export function blockiert(t: Kalendertermin): boolean {
  return !t.ganztaegig && t.zeigtAls !== 'free'
}

/**
 * **Die freien Lücken des Tages** – Arbeitszeit minus Termine.
 *
 * Termine, die sich überschneiden, werden verschmolzen: Zwei parallele Einladungen sind eine
 * belegte Stunde, keine zwei.
 */
export function luecken(termine: readonly Kalendertermin[], zeit: Spanne): Spanne[] {
  const belegt = termine
    .filter(blockiert)
    .map((t) => ({ von: stundeAus(t.beginn), bis: stundeAus(t.ende) }))
    .filter((s): s is Spanne => s.von !== null && s.bis !== null && s.bis > s.von)
    .map((s) => ({ von: Math.max(s.von, zeit.von), bis: Math.min(s.bis, zeit.bis) }))
    .filter((s) => s.bis > s.von)
    .sort((a, b) => a.von - b.von)

  const frei: Spanne[] = []
  let zeiger = zeit.von
  for (const s of belegt) {
    if (s.von > zeiger) frei.push({ von: zeiger, bis: s.von })
    zeiger = Math.max(zeiger, s.bis)
  }
  if (zeiger < zeit.bis) frei.push({ von: zeiger, bis: zeit.bis })
  return frei.filter((s) => s.bis - s.von >= MINDESTBLOCK)
}

/**
 * **Wie viele Stunden eines Arbeitstags durch Termine belegt sind** (AP 19.5).
 *
 * Gerechnet als *Fensterlänge minus freie Lücken* – also über
 * [`luecken`](#luecken) und **nicht** über eine zweite Summe der Termine. Zwei parallele
 * Einladungen sind eine belegte Stunde, keine zwei; diese Verschmelzung steht dort schon, und
 * sie ein zweites Mal zu schreiben wäre die Gelegenheit, dass beide Zahlen auseinanderlaufen.
 *
 * Die Lücken unterhalb von `MINDESTBLOCK` fallen dabei bewusst als *belegt* an: Sieben freie
 * Minuten zwischen zwei Besprechungen sind keine Arbeitszeit.
 */
export function belegteStunden(termine: readonly Kalendertermin[], zeit: Spanne): number {
  const laenge = Math.max(0, zeit.bis - zeit.von)
  if (laenge === 0) return 0
  const frei = luecken(termine, zeit).reduce((s, l) => s + (l.bis - l.von), 0)
  return Math.max(0, laenge - frei)
}

/**
 * **Die Reihenfolge – dieselbe wie im Vorlauf** (Z-20, 06.10.2026).
 *
 * Hier stand bis zum 06.10.2026 eine eigene Regel (Fälligkeit → Priorität → Änderungsdatum), die
 * Zeitfenster und Meilensteine nicht kannte. Nils sah es am Kalender: *„Ich glaube wir brauchen
 * ein einheitliches Vorlauf/Prio System."* Jetzt ist es `arbeitsRang` aus `shared` – dieselbe
 * Funktion, die der Vorlauf benutzt.
 */
export const rangAm = (stichtag: Date) => arbeitsRang(null, stichtag)

/**
 * **Wie viele Stücke eine Aufgabe höchstens werden darf** (Nils, 06.10.2026: *„aufgaben teilen
 * kannst du ja auch. muss ja nicht am stück sein (aber max ab mittel und mind. 30min slots) bei
 * großen aufgaben max. 3 stücke"*).
 *
 * Nach der **Größe**, nicht nach den Minuten: „ab mittel" ist Nils' Wort, und eine Größe ist das,
 * was jemand gesetzt hat. Ohne Größe zählt die eigene Schätzung gegen dieselben Schwellen.
 */
export function hoechstensStuecke(a: PlanAufgabe): number {
  const stufe = dauerstufeVon({ groesse: a.groesse })
  if (stufe === 'gross') return 3
  if (stufe === 'mittel') return 2
  if (stufe === 'ohne' && a.estimateMinutes) {
    if (a.estimateMinutes >= 240) return 3
    if (a.estimateMinutes >= 60) return 2
  }
  return 1
}

/** Dauer in **Stunden** – die Einheit des Plans. */
/**
 * **Dauer in Stunden – der Einheit des Plans** ([E-390](../../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * `dauern` ist die Tafel „Arbeitswelt → was eine Größe dort kostet". Fehlt sie oder die Welt
 * darin, gilt das Hausmaß – genau das macht `dauerMinutenVon` mit einer leeren Vorgabe, hier
 * muss also nichts geprüft werden.
 */
export const dauerStunden = (a: PlanAufgabe, dauern?: Weltdauern): number =>
  dauerMinutenVon(
    { groesse: a.groesse, estimateMinutes: a.estimateMinutes ?? null },
    (a.workspaceId && dauern?.[a.workspaceId]) || {},
  ) / 60

/**
 * **Eine Spanne aus den Lücken schneiden** – an Ort und Stelle. Eine Lücke, die sie in der Mitte
 * trifft, wird zu zwei; was dabei unter {@link MINDESTBLOCK} schrumpft, fällt weg wie in
 * {@link luecken}.
 */
function ausschneiden(frei: FreieLuecke[], s: Spanne): void {
  const neu: FreieLuecke[] = []
  for (const l of frei) {
    if (s.bis <= l.von || s.von >= l.bis) {
      neu.push(l)
      continue
    }
    if (s.von - l.von >= MINDESTBLOCK) neu.push({ ...l, bis: s.von })
    if (l.bis - s.bis >= MINDESTBLOCK) neu.push({ ...l, von: s.bis })
  }
  frei.splice(0, frei.length, ...neu)
}

/**
 * **Den Tag belegen.**
 *
 * Erst die Grundlast – sie ist kein Rest, den man hinten anhängt, sondern die Zeit, die den Tag
 * über wegläuft. Sie zuerst abzuziehen ist der Unterschied zwischen einem Plan, der aufgeht, und
 * einem, der jeden Tag um anderthalb Stunden zu optimistisch ist.
 *
 * Dann die Aufgaben nach Dringlichkeit in die verbleibenden Lücken. Was nicht mehr passt, steht
 * in `rest` – sichtbar, mit Dauer, statt still zu verschwinden.
 */
export function tagBelegen(
  aufgaben: readonly PlanAufgabe[],
  termine: readonly Kalendertermin[],
  zeit: Arbeitszeit | Tagesplanung = ARBEITSZEIT_VORGABE,
  vorschlaege: readonly PlanAufgabe[] = [],
  /**
   * **Was eine Größe je Arbeitswelt dauert** – leer heißt „überall das Hausmaß"
   * ([E-390](../../../../../docs/ENTSCHEIDUNGEN.md#e-390)). Als letzter Parameter mit Vorgabe,
   * damit jeder bestehende Aufruf unverändert weiterläuft.
   */
  dauern: Weltdauern = {},
  /**
   * **Was ich mir für diesen Tag vorgenommen habe** ([Z-20](../../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
   *
   * Nach dem Laufenden, vor den Vorschlägen: Was schon läuft, ist eine Tatsache, eine
   * Markierung eine Absicht, ein Vorschlag nur ein Angebot. Was davon nicht passt, landet in
   * `markiertPasstNicht` – anders als ein Vorschlag fällt eine Absicht nicht still weg.
   */
  markiert: readonly PlanAufgabe[] = [],
  /** Der Tag, für den gerechnet wird – für die Rangfolge (überfällig, heute fällig). */
  stichtag: Date = new Date(),
  /**
   * **Was an eine Uhrzeit gezogen wurde** (13.10.2026). Steht vor allem anderen fest – auch vor
   * der Grundlast, auch über einem Termin. Liegt es in einer Lücke, ist die Zeit damit belegt;
   * liegt es über einem Termin, kostet es nichts: dort arbeitet man nebenher.
   */
  fest: readonly FesteAufgabe[] = [],
): Tagesplan {
  const rang = rangAm(stichtag)
  /**
   * **Ein Fenster oder viele** – die Unterscheidung an einer Stelle, damit alle Aufrufe mit
   * einer schlichten `Arbeitszeit` unverändert weiterlaufen. Ein `Tagesplanung`-Wert trägt
   * `fenster`, eine `Arbeitszeit` nicht; mehr Kennzeichen braucht es nicht.
   */
  const planung: Tagesplanung = 'fenster' in zeit ? zeit : einFenster(zeit)

  const frei: FreieLuecke[] = planung.fenster.flatMap((f) =>
    luecken(termine, f).map((l) => ({ ...l, weltId: f.weltId })),
  )
  const freiRoh = frei.reduce((s, l) => s + (l.bis - l.von), 0)

  const bloecke: Block[] = termine
    .filter(blockiert)
    .map((t) => ({
      von: stundeAus(t.beginn) ?? 0,
      bis: stundeAus(t.ende) ?? 0,
      art: 'termin' as const,
      titel: t.betreff || 'Termin',
    }))
    .filter((b) => b.bis > b.von)

  for (const a of fest) {
    const ende = a.um + dauerStunden(a, dauern)
    bloecke.push({ von: a.um, bis: ende, art: 'fest', titel: a.title, aufgabe: a })
    ausschneiden(frei, { von: a.um, bis: ende })
  }

  /**
   * **Grundlast aus den vorderen Lücken – aber nur aus dem allgemeinen Fenster.**
   *
   * Mail, Telefon und Unterbrechung fallen einmal am Tag an. Sie auch aus dem PRIVAT-Abend zu
   * nehmen hieße, sie zweimal zu berechnen – und abends um sieben liest niemand den
   * Posteingang der Firma.
   */
  let offen = planung.grundlast
  for (const l of frei.filter((x) => x.weltId === null)) {
    if (offen <= 0) break
    const nimm = Math.min(offen, l.bis - l.von)
    if (nimm < MINDESTBLOCK) continue
    bloecke.push({
      von: l.von,
      bis: l.von + nimm,
      art: 'grundlast',
      titel: 'Mail, Telefon, Unterbrechung',
    })
    l.von += nimm
    offen -= nimm
  }

  /**
   * **Wie viele Vorschläge in keine Lücke passten** (Nils, 01.10.2026: *„irgendwie füllt der
   * noch nicht die Aufgaben korrekt da rein. Sicher, dass alles klappt?"*).
   *
   * Der Planer tat das Richtige – er sagte es nur nicht. Am 01.10.2026 hatten **32 von 35**
   * offenen Aufgaben keine Größe und galten deshalb als „mittel" = 2 Stunden; die freien
   * Strecken des Tages waren aber je **eine** Stunde. Also passte nichts, und der Tag sah leer
   * aus, obwohl 3,3 Stunden frei waren.
   *
   * Ein Plan, der schweigt, wird für kaputt gehalten – zu Recht.
   */
  let nichtPlatziert = 0

  /**
   * **Was vorbei ist, wird nicht mehr vergeben.** Erst hier, nach der Grundlast: Die gehört zum
   * Tag und soll da stehen, wo sie war – nur die Aufgaben richten sich nach der Uhr.
   */
  if (planung.abStunde !== undefined) {
    for (const l of frei) l.von = Math.max(l.von, planung.abStunde)
  }

  /** In welches Fenster diese Aufgabe darf – ihr eigenes, oder das allgemeine. */
  const fensterVon = (a: PlanAufgabe) =>
    a.workspaceId && planung.mitEigenerZeit.has(a.workspaceId) ? a.workspaceId : null

  /**
   * **Eine Aufgabe legen – am Stück, sonst in Stücken** (Z-20, 06.10.2026).
   *
   * Erst die früheste Lücke, in die sie ganz passt. Gibt es keine und darf sie geteilt werden
   * ({@link hoechstensStuecke}), dann der Reihe nach in die Lücken des Tages, jedes Stück
   * mindestens {@link MINDESTBLOCK}. Ein Stück wird so geschnitten, dass auch der **Rest** nie
   * unter die Mindestlänge fällt – sonst bliebe am Ende ein Viertelstündchen, das nirgends hin
   * darf.
   *
   * **Ganz oder gar nicht.** Reichen die erlaubten Stücke nicht, wird nichts gelegt und die
   * Lücken bleiben, wie sie waren. Eine halb gelegte Aufgabe sähe aus wie eine geplante.
   *
   * Der Anlass: Solingen (1 h, markiert, A, überfällig) stand am 06.10.2026 unter „passt nicht",
   * obwohl 1¾ Stunden frei waren – nur keine Stunde am Stück. Die Lücken füllten dann
   * Vorschläge à 15 Minuten.
   */
  const legen = (a: PlanAufgabe, art: BlockArt): boolean => {
    const d = dauerStunden(a, dauern)
    const ziel = fensterVon(a)
    const ganz = frei.find((l) => l.weltId === ziel && l.bis - l.von >= d - 1e-9)
    if (ganz) {
      bloecke.push({ von: ganz.von, bis: ganz.von + d, art, titel: a.title, aufgabe: a })
      ganz.von += d
      return true
    }
    const max = hoechstensStuecke(a)
    if (max < 2) return false
    const stuecke: { l: FreieLuecke; laenge: number }[] = []
    let offen = d
    for (const l of [...frei].sort((x, y) => x.von - y.von)) {
      if (offen <= 1e-9 || stuecke.length >= max) break
      if (l.weltId !== ziel) continue
      const platz = l.bis - l.von
      if (platz < MINDESTBLOCK - 1e-9) continue
      let laenge = Math.min(platz, offen)
      const rest = offen - laenge
      /** Bliebe ein Rest unter der Mindestlänge, wird dieses Stück kürzer – falls es dann noch reicht. */
      if (rest > 1e-9 && rest < MINDESTBLOCK - 1e-9) laenge = offen - MINDESTBLOCK
      if (laenge < MINDESTBLOCK - 1e-9) continue
      stuecke.push({ l, laenge })
      offen -= laenge
    }
    if (offen > 1e-9) return false
    stuecke.forEach(({ l, laenge }, i) => {
      bloecke.push({
        von: l.von,
        bis: l.von + laenge,
        art,
        titel: a.title,
        aufgabe: a,
        teil: { nr: i + 1, von: stuecke.length },
      })
      l.von += laenge
    })
    return true
  }

  const rest: PlanAufgabe[] = []
  for (const a of [...aufgaben].sort(rang)) if (!legen(a, 'aufgabe')) rest.push(a)

  const markiertPasstNicht: PlanAufgabe[] = []
  for (const a of [...markiert].sort(rang)) if (!legen(a, 'markiert')) markiertPasstNicht.push(a)

  /**
   * **Was der Tag noch hergibt, füllen Vorschläge** (Nils, 30.09.2026: *„die Aufgaben waren
   * noch nicht verteilt"*).
   *
   * Bis hierher belegte der Plan nur, was **in Arbeit** ist – genau wie das Mockup, das im
   * Beispielbestand acht Sachen in der Mitte hatte. In einem echten Tag, an dem nichts oder
   * kaum etwas läuft, blieb der Zeitstrahl damit leer. Und beim Blättern auf morgen wäre er
   * es immer: morgen ist nichts in Arbeit.
   *
   * Die Vorschläge landen **nie in `rest`**. „Passt heute nicht mehr" ist eine Aussage über
   * angefangene Arbeit – stünde dort auch jeder unberührte Vorrat, wäre die Liste hundert
   * Zeilen lang und damit stumm. Ein Vorschlag, der nicht mehr hineinpasst, fällt einfach weg;
   * er war ein Angebot, keine Zusage.
   */
  for (const a of [...vorschlaege].sort(rang)) if (!legen(a, 'vorschlag')) nichtPlatziert++

  /** Die längste noch freie Strecke – die Zahl, an der man sieht, warum nichts mehr passt. */
  const laengsteLuecke = frei.reduce((m, l) => Math.max(m, l.bis - l.von), 0)

  return {
    nichtPlatziert,
    laengsteLuecke,
    bloecke: bloecke.sort((x, y) => x.von - y.von),
    rest,
    markiertPasstNicht,
    freiRoh,
    freiNetto: Math.max(0, freiRoh - planung.grundlast),
  }
}

/* ── Gefährdet ────────────────────────────────────────────────────────── */

/** Eine Aufgabe, die rechnerisch zu spät drankommt. */
export interface Gefahr {
  aufgabe: PlanAufgabe
  /** Der Arbeitstag, an dem sie nach dieser Rechnung **fertig** wäre. */
  fertigAm: Date
  /** Ihre Frist. */
  frist: Date
  /** Wie viele Tage sie zu spät wäre – mindestens 1. */
  tageZuSpaet: number
}

/**
 * **Was rechnerisch zu spät kommt** ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md)
 * AP 19.8).
 *
 * Die Phasendatei sagt dazu: *„Fällt aus AP 19.6 ohne eigene Rechnung heraus."* Das stimmt im
 * Kern – es ist dieselbe Reihenfolge und dieselbe Dauer wie beim Tagesplan, nur über mehrere
 * Tage statt über einen.
 *
 * ## Die Rechnung
 *
 * Die Aufgaben werden nach Dringlichkeit sortiert und der Reihe nach auf die kommenden
 * **Arbeitstage** verteilt – Wochenenden und Feiertage fallen weg, dafür kennt
 * [`istWerktag`](../../../../../packages/shared/src/zeit.ts) sie seit AP 19.7. Jeder Arbeitstag
 * trägt `bis − von − grundlast` Stunden. Wo eine Aufgabe fertig wird, steht ihr Tag; liegt ihre
 * Frist davor, ist sie gefährdet.
 *
 * ## Was diese Rechnung **nicht** weiß
 *
 * **Die Termine künftiger Tage.** Work liest den Kalender tageweise; für eine Vorschau über
 * zwei Wochen zwanzig Abfragen zu stellen, wäre der falsche Tausch. Die Rechnung nimmt deshalb
 * einen durchschnittlichen Tag an – und ist damit eher **zu optimistisch**: Wer morgen vier
 * Stunden Besprechung hat, kommt noch später dran als hier steht.
 *
 * Das ist die richtige Richtung für eine Warnung. Eine Warnung, die zu früh anschlägt, nervt;
 * eine, die zu spät kommt, ist wertlos – aber eine, die **mehr** meldet, als eintritt, wäre das
 * Schlimmste: Nach zwei falschen Alarmen glaubt niemand mehr dem dritten. Lieber eine Meldung
 * weniger, die dafür stimmt.
 *
 * **Die eigene Abwesenheit weiß sie seit AP 19.5.** Sie ist der eine Fall, in dem der Fehler
 * nicht ein paar Stunden beträgt, sondern zwei Wochen: Wer am Freitag in den Urlaub fährt, ist
 * nicht am Montag mit der nächsten Aufgabe fertig. Und anders als der Kalender liegt sie
 * ohnehin schon im Browser – `absences.uebersicht` liefert −45 bis +120 Tage in **einem** Aufruf
 * für alle. Dafür war keine zusätzliche Abfrage nötig, nur der Blick darauf.
 *
 * `heute` wird hereingegeben, nicht hier geholt – wie überall in dieser Datei.
 */
export function gefaehrdet(
  aufgaben: readonly PlanAufgabe[],
  heute: Date,
  zeit: Arbeitszeit = ARBEITSZEIT_VORGABE,
  /**
   * **Dieselben Dauern wie im Plan** ([E-390](../../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
   * Rechnete die Gefahrenmeldung mit anderen Längen als die Belegung, widersprächen sich zwei
   * Zahlen auf demselben Schirm – und man glaubte keiner mehr.
   */
  dauern: Weltdauern = {},
  /**
   * **Wie viel von einem Tag übrig ist** – `1` normal, `0.5` am halben Urlaubstag, `0` am ganzen.
   *
   * Eine Funktion statt einer Liste von Tagen, weil die Rechnung vorwärts läuft und nicht weiß,
   * wie weit sie kommt: Eine Aufgabe von vierzig Stunden hinter drei Wochen Urlaub schiebt die
   * Schleife bis in den übernächsten Monat. Wer vorher eine Liste bauen müsste, müsste raten,
   * wie lang sie wird.
   */
  anteil: (tag: Date) => number = () => 1,
  /**
   * **Wie viele Stunden an diesem Tag schon durch Termine weg sind** (AP 19.5).
   *
   * Bis `termineImZeitraum` gab es diese Zahl nicht – die Rechnung nahm einen durchschnittlichen
   * Tag an. Für Tage jenseits des geholten Zeitraums gilt das weiter, und das ist in Ordnung:
   * Die Vorschau ist dann eher zu optimistisch, und das ist die richtige Richtung. Eine Warnung,
   * die zu oft anschlägt, glaubt beim dritten Mal niemand mehr.
   */
  belegt: (tag: Date) => number = () => 0,
  /**
   * **Was für heute markiert ist, kommt zuerst dran** (Z-20, 06.10.2026). Sonst stand an einer
   * für heute markierten Aufgabe „kommt erst am Di. dran" – die Rechnung wusste nichts von der
   * Absicht.
   */
  heuteMarkiert: ReadonlySet<string> = new Set(),
): Gefahr[] {
  const proTag = Math.max(0, zeit.bis - zeit.von - zeit.grundlast)
  if (proTag <= 0) return []

  /**
   * **Was ein einzelner Kalendertag hergibt** – an einer Stelle, weil drei Dinge zusammenkommen,
   * die vorher an drei Stellen standen: Werktag, eigene Abwesenheit, fremde Termine. Ein Tag,
   * der nichts hergibt, wird übersprungen; dabei ist es gleich, ob ein Sonntag, ein Urlaub oder
   * ein durchgehend verplanter Donnerstag der Grund ist.
   */
  const kapazitaet = (d: Date): number =>
    istWerktag(d) ? Math.max(0, proTag * anteil(d) - belegt(d)) : 0

  const tag = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate())
  let frei = kapazitaet(tag)
  const gefahren: Gefahr[] = []

  const rang = rangAm(heute)
  const reihe = [...aufgaben].sort(
    (a, b) => Number(heuteMarkiert.has(b.id)) - Number(heuteMarkiert.has(a.id)) || rang(a, b),
  )
  for (const a of reihe) {
    let offen = dauerStunden(a, dauern)
    /**
     * Eine Aufgabe, die länger dauert als ein Tag hergibt, läuft über mehrere – deshalb die
     * Schleife und nicht „passt / passt nicht". Die Obergrenze von 400 Durchläufen ist eine
     * Reißleine gegen eine Endlosschleife, kein fachliches Limit: Bei 7,5 h je Tag sind das
     * reichlich zwei Jahre.
     */
    for (let schutz = 0; offen > 0 && schutz < 400; schutz++) {
      if (frei <= 0) {
        /**
         * **Weiter, bis ein Tag etwas hergibt.** Die Reißleine zählt mit: Drei Wochen Urlaub
         * sind einundzwanzig Durchläufe, die nichts beitragen, und ohne sie liefe ein
         * Dauerurlaub ewig.
         */
        let uebersprungen = 0
        do {
          tag.setDate(tag.getDate() + 1)
          uebersprungen++
        } while (kapazitaet(tag) <= 0 && uebersprungen < 400)
        frei = kapazitaet(tag)
        if (frei <= 0) break
      }
      const nimm = Math.min(offen, frei)
      offen -= nimm
      frei -= nimm
    }

    /**
     * **Der wirksame Termin, nicht nur die Fälligkeit** (Z-20) – derselbe, nach dem sortiert
     * wurde. Ein Zeitfenster „5 Tage" ist eine Zusage wie ein Fälligkeitsdatum.
     */
    const termin = a.termin !== undefined ? a.termin?.datum : a.dueAt ? new Date(a.dueAt) : null
    if (!termin) continue
    const frist = new Date(termin)
    const fristTag = new Date(frist.getFullYear(), frist.getMonth(), frist.getDate())
    if (fristTag.getTime() >= tag.getTime()) continue
    gefahren.push({
      aufgabe: a,
      fertigAm: new Date(tag),
      frist,
      tageZuSpaet: Math.max(
        1,
        Math.round((tag.getTime() - fristTag.getTime()) / (24 * 60 * 60 * 1000)),
      ),
    })
  }

  return gefahren
}
